// React import
import React, { useState } from 'react';

// Import MUI
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { red, grey } from '@mui/material/colors';
import { useTheme, alpha } from '@mui/material/styles';

// Fontawesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircle } from '@fortawesome/pro-light-svg-icons';
import { faCircleDot } from '@fortawesome/pro-regular-svg-icons';

// Generic import
import _ from 'lodash';
import { validityField } from '../utils/date.js';
import axios from 'axios';
import queryString from 'query-string';
import validator from 'email-validator';

// Local import
import currencyToText from '../CurrencyToText.js';
import { calcSectionSum, calcOfferSum } from '../utils/opportunity.js';

// Display formatting only — amounts shown to the customer are rendered to two
// decimals (US$569.60). Nothing here changes how a value is calculated.
const money = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function OfferOpportunityOneOff(props) {
    const { opportunity, org, status, setStatus, print, exchangeRates, temporaryAccesstoken, selected, setSelected, html, showEmptySections, assignFieldToSectionNode, removeFieldFromSectionNode } = props;
    const parsed = queryString.parse(window.location.search);

    const [ saving, setSaving ] = useState(false);
    const [ saveSuccess, setSaveSuccess ] = useState(false);
    const [ saveError, setSaveError ] = useState(null);
    const [ showRejectDialog, setShowRejectDialog ] = useState(false);
    const [ rejectReason, setRejectReason ] = useState(null);
    const [ rejectReasonText, setRejectReasonText ] = useState('');
    const [ showAcceptDialog, setShowAcceptDialog ] = useState(false);
    const [ customerName, setCustomerName ] = useState('');
    const [ customerEmail, setCustomerEmail ] = useState('');
    const [ customerEmailError, setCustomerEmailError ] = useState('');
    const [ rejectCustomerName, setRejectCustomerName ] = useState('');
    const [ rejectCustomerEmail, setRejectCustomerEmail ] = useState('');
    const [ rejectCustomerEmailError, setRejectCustomerEmailError ] = useState('');

    function onAccept() {
        const sentSelected = fixSelected({ opportunity, selected });
        if (!sentSelected && opportunity.type !== 'one-off-free-text') {
            setSaveError('select');
            return;
        }

        setCustomerName('');
        setCustomerEmail('');
        setCustomerEmailError('');
        setShowAcceptDialog(true);
    }

    async function onAcceptConfirm() {
        const trimmedEmail = customerEmail.trim();
        if (!customerName.trim() || !trimmedEmail) {
            return;
        }

        if (!validator.validate(trimmedEmail)) {
            setCustomerEmailError('Please enter a valid email address');
            return;
        }

        const sentSelected = fixSelected({ opportunity, selected });
        if (!sentSelected) {
            setSaveError('select');
            return;
        }

        setSaveError(null);
        setCustomerEmailError('');
        setSaving(true);
        setShowAcceptDialog(false);

        try {
            await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&temporaryAccesstoken=${temporaryAccesstoken}`, {
                status: 'endcustomer-accepted',
                selected: sentSelected,
                name: customerName.trim(),
                email: trimmedEmail,
            });

            setStatus('endcustomer-accepted');
            setSaving(false);
            setSaveSuccess(true);
        } catch(e) {
            setSaveError('generic');
            setSaving(false);
        }
    }

    function onReject() {
        setRejectReason(null);
        setRejectReasonText('');
        setRejectCustomerName('');
        setRejectCustomerEmail('');
        setRejectCustomerEmailError('');
        setShowRejectDialog(true);
    }

    async function onRejectOK() {
        if (!rejectReason || (rejectReason === 'Other' && rejectReasonText.length < 5)) {
            return;
        }
        const trimmedEmail = rejectCustomerEmail.trim();
        if (!rejectCustomerName.trim() || !trimmedEmail) {
            return;
        }

        if (!validator.validate(trimmedEmail)) {
            setRejectCustomerEmailError('Please enter a valid email address');
            return;
        }

        setSaving(true)
        setRejectCustomerEmailError('');
        setShowRejectDialog(false);
        // The offer the customer had ticked before rejecting, when there are several: reports count
        // the rejected quote at that offer instead of the first.
        const tickedOffer = opportunity.offers.length > 1 ? opportunity.offers.find(o => selected.includes(o.id)) : null;
        try {
            await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&temporaryAccesstoken=${temporaryAccesstoken}`, {
                status: 'endcustomer-rejected',
                selected: [],
                ...(tickedOffer ? { lostOffer: tickedOffer.id } : {}),
                reason: rejectReason === 'Other' ? rejectReasonText : rejectReason,
                name: rejectCustomerName.trim(),
                email: trimmedEmail,
            });
            setStatus('endcustomer-rejected');
            setSaveSuccess(true);
            setSaveError(null);
            setSaving(false);
        } catch (e) {
            console.log(e);
            setSaveError('generic');
            setSaveSuccess(false);
            setSaving(false);
        }
    }

    function onSnackbarClose() {
        setSaveError(null);
        setSaveSuccess(false);
    }

    return (
        <div className='offer-page'>
            {parsed.type === 'preview' && (
                <>
                    <Alert severity='warning'>
                        Note: This is a preview page. Please do not send its link to anyone else.
                    </Alert>
                    <div style={{ position: 'fixed', top: '0px', right: '0px', left: '0px', bottom: '0px', fontSize: '96px', zIndex: 10, transformOrigin: 'center', transform: 'rotate(-45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', opacity: '0.2' }}>
                        PREVIEW
                    </div>
                </>
            )}
            <Grid container>
                <Grid item sm={10} />
                <Grid item sm={2}>
                    <div 
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', padding: '4px 10px' }}
                        onClick={() => window.open('https://www.monada.ai', '_blank').focus()}
                    >
                        <Typography variant='body' sx={{ whiteSpace: 'nowrap', paddingRight: '3px', fontSize: '11px' }}>
                            Powered by
                        </Typography>
                        <Typography variant='body' style={{ color: '#605DEC', fontSize: '14px', fontFamily: '\'DM Serif Display\', serif', fontWeight: '400' }}>
                                Monada
                        </Typography>
                    </div>
                </Grid>
            </Grid>
            <Box sx={{ padding: '20px 20px 12px 20px' }}>
                <div dangerouslySetInnerHTML={{__html: html.header }} />

                {(opportunity.type !== 'one-off-free-text' || parsed.pdf) && opportunity.offers.map(offer => (
                    <Offer 
                        offer={offer} 
                        offers={opportunity.offers} 
                        transportationMethod={opportunity.transportationMethod} 
                        products={opportunity.products} 
                        exchangeRates={exchangeRates} 
                        selected={selected}
                        setSelected={setSelected}
                        print={parsed.print || parsed.pdf}
                        emphasizeOfferChoices={
                            (opportunity.offers.length > 1) && (!opportunity.offers.find(o => selected.includes(o.id))) && (saveError === 'select')
                        }
                        emphasizeSectionOfferChoices={
                            (opportunity.offers.length === 1 || selected.includes(offer.id)) && (saveError === 'select')
                        }
                        html={html}
                        status={opportunity.status}
                        lockedExchangeRates={opportunity.exchangeRates}
                        organization={org}
                        showEmptySections={showEmptySections}
                        assignFieldToSectionNode={assignFieldToSectionNode}
                        removeFieldFromSectionNode={removeFieldFromSectionNode}
                        viewTemplate={opportunity?.attributes?.viewTemplate || 'standard'}
                    />
                ))}
                {opportunity.type === 'one-off-free-text' && !parsed.pdf && (
                    <Box sx={{ 
                        padding: '24px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '8px', 
                        fontSize: '12px', 
                        fontFamily: 'Poppins, sans-serif', 
                        '& *': { fontSize: '12px !important', fontFamily: 'Poppins, sans-serif !important' },
                        // A table pasted from a spreadsheet reaches the customer as bare
                        // markup, so give it the same borders it has in the builder.
                        '& table': { borderCollapse: 'collapse', margin: '8px 0px' },
                        '& table td, & table th': { border: '1px solid #d0d5dd', padding: '4px 8px', verticalAlign: 'top' },
                        '& table th': { background: '#f7f8fa', fontWeight: 600, textAlign: 'left' },
                        '& img': { maxWidth: '100%', height: 'auto' },
                        '& span[data-field="true"]': {
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                opacity: 0.9
                            }
                        },
                        '& span[style*="borderRadius"]': {
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                opacity: 0.9
                            }
                        }
                    }}>
                        <div dangerouslySetInnerHTML={{ __html: html.emailBody }} />
                    </Box>
                )}
                {(!(opportunity?.attributes?.viewTemplate === 'contract')) && (
                    <Grid container>
                        <Grid item sm={12}>
                            {saveError === 'select' && (!fixSelected({ selected, opportunity })) && (
                                <Alert onClose={onSnackbarClose} severity='error' variant='filled'>
                                    Please select the option you would like to accept from the choices above.
                                </Alert>
                            )}
                            {((status !== 'completed') && (status !== 'endcustomer-accepted') && (status !== 'endcustomer-rejected')) && (!parsed.print) && !parsed.pdf && (
                                <Button
                                    disabled={saving}
                                    color='primary'
                                    variant='contained'
                                    disableElevation
                                    fullWidth
                                    onClick={() => onAccept()}
                                    sx={{
                                        marginTop: saveError === 'select' ? '8px' : '20px',
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        padding: '14px',
                                        borderRadius: '8px',
                                        boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
                                        '&:hover': { boxShadow: '0px 4px 12px rgba(16, 24, 40, 0.16)' },
                                    }}
                                >
                                    {saving ? <CircularProgress size={22} color='inherit' /> : 'Book Now'}
                                </Button>
                            )}
                            {(((status === 'completed') || (status === 'endcustomer-accepted'))) && (!parsed.print) && !parsed.pdf && (
                                <Box sx={{ marginTop: '20px', fontSize: '15px', fontWeight: 600, padding: '14px', borderRadius: '8px', textAlign: 'center', backgroundColor: '#ECFDF3', color: '#027A48', border: '1px solid #ABEFC6' }}>
                                    Price Quote Accepted
                                </Box>
                            )}
                            {(status === 'endcustomer-rejected') && (!parsed.print) && !parsed.pdf && (
                                <Box sx={{ marginTop: '20px', fontSize: '15px', fontWeight: 600, padding: '14px', borderRadius: '8px', textAlign: 'center', backgroundColor: '#FEF3F2', color: '#B42318', border: '1px solid #FECDCA' }}>
                                    Price Quote Rejected
                                </Box>
                            )}
                            {status !== 'completed' && status !== 'endcustomer-accepted' && status !== 'endcustomer-rejected' && (!parsed.print) && !parsed.pdf && (!saving) && (
                                <Box sx={{ marginTop: '8px', fontSize: '12px', color: '#374151', textAlign: 'center' }}>
                                    Please review the offers above. Click <Box component='span' sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }} onClick={() => onAccept()}>Book Now</Box> to select an offer, or <Box component='span' sx={{ color: '#B42318', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }} onClick={() => onReject()}>request a revised offer</Box>.
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                )}
                <Grid container>
                    <Grid item sm={12} xs={12} style={{ paddingBottom: '16px' }}>
                        <Typography component='div' sx={{ fontSize: '10px', color: '#374151' }}>
                            Created and sent by {opportunity.createdBy}.
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container>
                    {html.terms && (
                        <Grid item sm={12} xs={12} style={{ paddingBottom: '20px', fontFamily: 'Poppins, sans-serif' }}>
                            <Typography component={Box} sx={{ fontSize: '11px', fontWeight: 600, color: '#333333', marginBottom: '4px' }}>
                                Terms &amp; Conditions
                            </Typography>
                            <div className='terms-body' dangerouslySetInnerHTML={{ __html: html.terms }} />
                        </Grid>
                    )}
                    <div 
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer', padding: '4px 10px' }}
                        onClick={() => window.open('https://www.monada.ai', '_blank').focus()}
                    >
                        <Typography variant='body' sx={{ whiteSpace: 'nowrap', paddingRight: '3px', fontSize: '11px' }}>
                            Powered by
                        </Typography>
                        <Typography variant='body' style={{ color: '#605DEC', fontSize: '14px', fontFamily: '\'DM Serif Display\', serif', fontWeight: '400' }}>
                                Monada
                        </Typography>
                    </div>
                </Grid>
            </Box>
            <Snackbar open={saveError === 'generic'} autoHideDuration={6000} onClose={onSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} >
                <Alert onClose={onSnackbarClose} severity='error' variant='filled' sx={{ width: '100%' }}>
                    Error saving. Please try again.
                </Alert>
            </Snackbar>
            <Snackbar open={saveSuccess && status === 'endcustomer-accepted'} autoHideDuration={6000} onClose={onSnackbarClose}>
                <Alert onClose={onSnackbarClose} severity='success' variant='filled' sx={{ width: '100%' }}>
                    Congrats! You have accepted this price quote.
                </Alert>
            </Snackbar>
            <Snackbar open={saveSuccess && status === 'endcustomer-rejected'} autoHideDuration={6000} onClose={onSnackbarClose}>
                <Alert onClose={onSnackbarClose} severity='success' variant='filled' sx={{ width: '100%' }}>
                    You have rejected this price quote.
                </Alert>
            </Snackbar>
            <Dialog open={showAcceptDialog} onClose={() => setShowAcceptDialog(false)}>
                <DialogTitle>
                    Book Now
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {opportunity.type === 'one-off-free-text' && opportunity.offers.length > 1 && (
                            <Box>
                                <Box>
                                    Please select the offer you would like to accept:
                                </Box>
                                <Box sx={{ marginBottom: '16px' }}>
                                    <RadioGroup 
                                        value={_.intersection(selected, opportunity.offers.map(o => o.id))[0]} 
                                        onChange={(e) => setSelected(select => {
                                            const diff = _.difference(select, opportunity.offers.map(o => o.id));
                                            return _.compact([...diff, e.target.value]);
                                        })}>
                                        {opportunity.offers.map(offer => (
                                            <FormControlLabel key={offer.id} value={offer.id} control={<Radio />} label={offer.title} />
                                        ))}
                                    </RadioGroup>
                                </Box>
                            </Box>
                        )}
                        <Box sx={{ marginBottom: '16px' }}>
                            Please provide your contact information to proceed with booking.
                        </Box>
                        <Box sx={{ marginBottom: '16px' }}>
                            <TextField 
                                fullWidth 
                                label='Name' 
                                variant='outlined' 
                                value={customerName} 
                                onChange={(e) => setCustomerName(e.target.value)}
                                required
                                sx={{ marginBottom: '16px' }}
                            />
                            <TextField 
                                fullWidth 
                                label='Email' 
                                type='email'
                                variant='outlined' 
                                value={customerEmail} 
                                onChange={(e) => {
                                    setCustomerEmail(e.target.value);
                                    if (customerEmailError && validator.validate(e.target.value.trim())) {
                                        setCustomerEmailError('');
                                    }
                                }}
                                error={!!customerEmailError}
                                helperText={customerEmailError}
                                required
                            />
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAcceptDialog(false)}>
                        Cancel
                    </Button>
                    <Button 
                        disabled={!customerName.trim() || !customerEmail.trim()}
                        onClick={() => onAcceptConfirm()} 
                        variant='contained'
                        autoFocus
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)}>
                <DialogTitle>
                    Reject Offer
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Box>
                            Can you please provide us with the reason you are rejecting our offer?
                        </Box>
                        <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
                            <FormControl>
                                <RadioGroup
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                >
                                    <FormControlLabel value='Transit time too long' control={<Radio />} label='Transit time too long' />
                                    <FormControlLabel value='Prefer not to work with this carrier' control={<Radio />} label='Prefer not to work with this carrier' />
                                    <FormControlLabel value='Offer is too expensive' control={<Radio />} label='Offer is too expensive' />
                                    <FormControlLabel value='Other' control={<Radio />} label='Other' />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                        {rejectReason === 'Other' && (
                            <TextField 
                                fullWidth 
                                label='Can you please tell us the reason?' 
                                variant='outlined' 
                                value={rejectReasonText} 
                                onChange={(e) => setRejectReasonText(e.target.value)}
                                sx={{ marginBottom: '16px' }}
                            />
                        )}
                        <Box sx={{ marginTop: '16px' }}>
                            <TextField 
                                fullWidth 
                                label='Name' 
                                variant='outlined' 
                                value={rejectCustomerName} 
                                onChange={(e) => setRejectCustomerName(e.target.value)}
                                required
                                sx={{ marginBottom: '16px' }}
                            />
                            <TextField 
                                fullWidth 
                                label='Email' 
                                type='email'
                                variant='outlined' 
                                value={rejectCustomerEmail} 
                                onChange={(e) => {
                                    setRejectCustomerEmail(e.target.value);
                                    if (rejectCustomerEmailError && validator.validate(e.target.value.trim())) {
                                        setRejectCustomerEmailError('');
                                    }
                                }}
                                error={!!rejectCustomerEmailError}
                                helperText={rejectCustomerEmailError}
                                required
                            />
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRejectDialog(false)}>
                        Cancel
                    </Button>
                    <Button 
                        disabled={!rejectReason || (rejectReason === 'Other' && rejectReasonText.length < 5) || !rejectCustomerName.trim() || !rejectCustomerEmail.trim() || !validator.validate(rejectCustomerEmail.trim())} 
                        onClick={() => onRejectOK()} 
                        variant='contained'
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

function Offer(props) {
    const { 
        offer, offers, transportationMethod, products, selected, setSelected, exchangeRates, print, showEmptySections, assignFieldToSectionNode, 
        removeFieldFromSectionNode, emphasizeOfferChoices, emphasizeSectionOfferChoices, html, status, lockedExchangeRates, organization, viewTemplate
    } = props;

    const preCalcs = {};
    _.forEach(html.sectionOfferSumInOfferDisplayCurrency, (value, key) => {
        if (key.startsWith(offer.id)) {
            preCalcs[key.split(':')[1]] = preCalcs[key.split(':')[1]] || {};
            preCalcs[key.split(':')[1]][key.split(':')[2]] = value;
        }
    });

    const offerSum = calcOfferSum({ offer, transportationMethod, products, selected, exchangeRates, preCalcs, organization });

    const offerSumText = (() => {
        if (Math.round(offerSum.min.normalized) === Math.round(offerSum.max.normalized)) {
            return _.map(offerSum.min.sum, (value, currency) => currencyToText(currency, true, money(value))).join(', ');
        }

        else return `From ${_.map(offerSum.min.sum, (value, currency) => currencyToText(currency, true, money(value))).join(', ')}`
    })();

    const disabled = status === 'endcustomer-accepted' || status === 'endcustomer-rejected' || status === 'completed';
    // viewTemplate affects all rendering (online view, PDF, and print), not just PDF
    const isContractTemplate = viewTemplate === 'contract';
    return (
        <Paper style={{ padding: '13px 22px', margin: '10px 0px', ...(print ? { boxShadow: 'none', border: 'none' } : {}) }}>
            {!isContractTemplate && (
                <MenuItem
                    disabled={disabled}
                    sx={{
                        color: '#333333',
                        fontSize: '18px',
                        fontWeight: 600,
                        padding: '6px 0px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center', 
                        justifyContent: 'start',
                        whiteSpace: 'normal'
                    }}
                    onClick={() => setSelected(select => ([..._.difference(selected, offers.map(o => o.id)), offer.id]))}
                >
                    {offers.length > 1 && !print && (
                        <FontAwesomeIcon
                            icon={selected.includes(offer.id) ? faCircleDot : faCircle} 
                            style={{ 
                                marginRight: '12px', 
                                flex: 0, 
                                color: disabled ? grey[500] : emphasizeOfferChoices ? red[500] : selected.includes(offer.id) ? '#605DE4' : '' 
                            }} 
                        />
                    )}
                    <Box sx={{ flex: 1 }}>{offer.title}</Box>
                </MenuItem>
            )}
            {isContractTemplate && (
                <Box sx={{
                    color: '#333333',
                    fontSize: '18px',
                    fontWeight: 600,
                    padding: '6px 0px',
                }}>
                    {offer.title}
                </Box>
            )}
            <Box sx={{ fontSize: '14px', color: '#333333', fontStyle: 'italic', marginBottom: '14px' }} >
                {offer.description}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#333333', display: 'flex', alignItems: 'center' }} >
                {offerDetails(offer).map((field, index) => (
                    <Box sx={{ borderLeft: index === 0 ? '' : `1px solid ${grey[300]}`, paddingLeft: index === 0 ? '' : '12px', marginLeft: index === 0 ? '' : '12px' }}>
                        {field.title}&nbsp;{field.value}
                    </Box>
                ))}
            </Box>
            {offer.sections.map(section => (
                <Section
                    key={section.id}
                    section={section}
                    transportationMethod={transportationMethod}
                    products={products} 
                    selected={selected}
                    setSelected={setSelected}
                    exchangeRates={exchangeRates}
                    emphasizeOfferChoices={
                        (emphasizeSectionOfferChoices) && (section.offers.length > 1) && (!section.offers.find(o => selected.includes(o.id)))
                    }
                    offerId={offer.id}
                    html={html}
                    status={status}
                    showChoices={offers.length === 1 || selected.includes(offer.id)}
                    showEmptySections={showEmptySections}
                    assignFieldToSectionNode={assignFieldToSectionNode}
                    removeFieldFromSectionNode={removeFieldFromSectionNode}
                    viewTemplate={viewTemplate}
                />
            ))}

            {!isContractTemplate && (
                <OfferSummary 
                    offer={offer}
                    transportationMethod={transportationMethod}
                    products={products} 
                    selected={selected}
                    setSelected={setSelected}
                    exchangeRates={exchangeRates}
                    html={html}
                    lockedExchangeRates={lockedExchangeRates}
                    organization={organization}
                />
            )}

            {!isContractTemplate && (
                <Box className='offer-total' sx={{ paddingTop: '14px', marginTop: '14px', borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ 
                        fontSize: '16px', 
                        color: '#000000', 
                        fontWeight: 700, 
                        flex: 0,
                        padding: '12px 40px',
                    }}>
                        TOTAL
                    </Box>
                    <Box sx={{
                        background: 'rgb(233, 232, 250)',
                        fontSize: '16px',
                        fontWeight: '700',
                        lineHeight: '24px',
                        padding: '12px 40px',
                        borderRadius: '24px',
                        minWidth: '311px',
                        textAlign: 'center',
                        flex: 0,
                    }}>
                        {offerSumText}
                    </Box>
                </Box>
            )}
        </Paper>
    )
}

function Section(props) {
    const { offerId, section, products, selected, setSelected, showChoices, emphasizeOfferChoices, html, status, 
        showEmptySections, assignFieldToSectionNode, removeFieldFromSectionNode, viewTemplate } = props;

    const header = (
        <Box className='section-title' sx={{ display: 'flex' }}>
            <Typography component={Box} sx={{ fontSize: '14px', color: '#333333', fontWeight: 600 }}>
                {section.title}
            </Typography>
            <Typography component={Box} sx={{ fontSize: '14px', color: '#333333', paddingLeft: '5px' }}>
                {section.description ? ` | ${section.description}` : ''}
            </Typography>
        </Box>
    )

    if (section.offers.length === 1) {
        const fields = _.compact(section.offers[0].fields.map(field => html.fields[field.id]));
        if (fields.length === 0 && !showEmptySections) return;

        return (
            <Box key={section.id} sx={{ padding: '10px 0px', marginTop: '6px', borderRadius: '6px' }}>
                {header}
                {/* The section's own transit time, validity, route and carrier, as set in the builder. */}
                {offerDetails(section.offers[0]).length > 0 && (
                    <Box sx={{ fontSize: '13px', color: '#333333', display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginTop: '4px' }}>
                        {offerDetails(section.offers[0]).map((field, index) => (
                            <Box key={field.title} sx={{ borderLeft: index === 0 ? '' : `1px solid ${grey[300]}`, paddingLeft: index === 0 ? '' : '12px', marginLeft: index === 0 ? '' : '12px' }}>
                                {field.title}&nbsp;{field.value}
                            </Box>
                        ))}
                    </Box>
                )}
                <Box sx={{ padding: '0px 16px' }}>
                    {!section.hideBreakdown && (
                        <Box sx={{ padding: '4px 0px', marginTop: '4px' }} >
                            <SectionCharges fields={section.offers[0].fields} productCount={(products || []).length} headerHtml={html?.sectionProductHeaders?.[section.id]?.[section.offers[0].id]} html={html} />
                            {assignFieldToSectionNode ? (
                                <Box sx={{ display: 'flex', marginTop: '12px' }}>
                                    <Button 
                                        {...assignFieldToSectionNode} 
                                        onClick={() => assignFieldToSectionNode.onClick(section.id)}
                                    />
                                </Box>
                            ) : null}
                            {removeFieldFromSectionNode ? (
                                <Box sx={{ display: 'flex', marginTop: '12px' }}>
                                    <Button 
                                        {...removeFieldFromSectionNode} 
                                        onClick={() => removeFieldFromSectionNode.onClick(section.id)}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }
    const disabled = status === 'endcustomer-accepted' || status === 'endcustomer-rejected' || status === 'completed';
    const isContractTemplate = viewTemplate === 'contract';

    return (
        <Box key={section.id} sx={{ padding: '10px 0px', marginTop: '13px', borderRadius: '6px' }}>
            {header}
            {!isContractTemplate && (
                <Box sx={{ margin: '8px 0px 12px 0px' }}>
                    We have multiple offers for you. Please select from one of them:
                </Box>
            )}
            <Box sx={{ padding: '0px 16px' }}>
                {section.offers.map(sectionOffer => {
                    const { sum } = html.sectionOfferSum[`${offerId}:${section.id}:${sectionOffer.id}`];
                    return (
                        <Accordion 
                            elevation={0}
                            variant='outlined'
                            sx={{ 
                                border: `1px solid ${grey[300]}`, 
                                borderRadius: '6px', 
                                marginBottom: '13px',
                                ':before': {
                                    display: 'none'
                                }
                            }}
                            disableGutters={true} 
                            expanded={true}
                        >
                            <Box expandIcon={null} sx={{ padding: '0px' }}>
                                {isContractTemplate ? (
                                    <Box sx={{ padding: '14px' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                            {sectionOffer.title}
                                            <Typography variant='caption' component={Box} sx={{ display: 'flex' }}>
                                                {sectionOffer.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <MenuItem
                                        disabled={disabled}
                                        key={sectionOffer.id}
                                        sx={{ 
                                            padding: '14px', 
                                            width: '100%', 
                                            borderRadius: '6px', 
                                            display: 'flex', 
                                            alignItems: 'center',
                                            borderColor: emphasizeOfferChoices ? red[500] : '',
                                            whiteSpace: 'normal'

                                        }}
                                        onClick={e => {
                                            setSelected(select => ([..._.difference(selected, section.offers.map(o => o.id)), sectionOffer.id]));
                                            e.stopPropagation();
                                        }}
                                    >
                                        {showChoices && (
                                            <FontAwesomeIcon
                                                icon={selected.includes(sectionOffer.id) ? faCircleDot : faCircle} 
                                                style={{ 
                                                    marginRight: '12px', 
                                                    flex: 0, 
                                                    color: disabled ? grey[500] : emphasizeOfferChoices ? red[500] : selected.includes(sectionOffer.id) ? '#605DE4' : '' 
                                                }} 
                                            />
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                            {sectionOffer.title}
                                            <Typography variant='caption' component={Box} sx={{ display: 'flex' }}>
                                                {sectionOffer.description}
                                                {offerDetails(sectionOffer).map((field, index) => (
                                                    <Box sx={{
                                                        borderLeft: index === 0 && !sectionOffer.description ? '' : `1px solid ${grey[300]}`,
                                                        paddingLeft: index === 0 && !sectionOffer.description ? '' : '12px',
                                                        marginLeft: index === 0 && !sectionOffer.description ? '' : '12px'
                                                    }}>
                                                        {field.title}&nbsp;{field.value}
                                                    </Box>
                                                ))}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 0, whiteSpace: 'nowrap' }} >
                                            {_.map(sum, (value, currency) => currencyToText(currency, true, money(value))).join(' | ')}
                                        </Box>
                                    </MenuItem>
                                )}
                            </Box>
                            <AccordionDetails>
                                {!section.hideBreakdown && (
                                    <SectionCharges fields={sectionOffer.fields} productCount={(products || []).length} headerHtml={html.sectionProductHeaders[section.id][section.offers[0].id]} html={html} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )
                })}
            </Box>
        </Box>
    );
}

function fixSelected({ opportunity, selected }) {
    const sentSelected = _.cloneDeep(selected);
    if (opportunity.offers.length === 1) sentSelected.push(opportunity.offers[0].id);

    const selectedOffer = (() => {
        const temp = opportunity.offers.filter(o => sentSelected.includes(o.id));
        return temp.length === 1 ? temp[0] :  null;
    })();

    if (!selectedOffer) {
        return null;
    }

    const err = selectedOffer.sections?.find(section => {
        if (section.offers.length === 1) sentSelected.push(section.offers[0].id);
        const selectedSectionOffer = (() => {
            const temp = section.offers.filter(o => sentSelected.includes(o.id));
            return temp.length === 1 ? temp[0] :  null;
        })();

        if (!selectedSectionOffer) {
            return true;
        }
        return false;
    });

    if (err) return null;
    return sentSelected;
}

function OfferSummary(props) {
    const { offer, transportationMethod, products, selected, exchangeRates, html, lockedExchangeRates, organization } = props;
    const theme = useTheme();

    if (_.every(html.sectionOfferSum, v => !v.showSection)) return null;

    return (
        <Box className='offer-summary' sx={{ backgroundColor: alpha(theme.palette.text.primary, 0.01), padding: '16px 20px' }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>
                Offer Summary
            </Typography>
            <TableContainer>
                <Table className='charge-table'>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: '#374151', fontWeight: 600, fontSize: '12px', backgroundColor: '#F7F8FA' }}>
                                Charges
                            </TableCell>
                            <TableCell sx={{ color: '#374151', fontWeight: 600, fontSize: '12px', backgroundColor: '#F7F8FA', textAlign: 'right' }}>
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {offer.sections.map(section => {
                            const preCalcs = {};
                            _.forEach(html.sectionOfferSum, (value, key) => {
                                if (key.startsWith(`${offer.id}:${section.id}:`)) preCalcs[key.split(':')[2]] = value;
                            });
                            const sectionSum = calcSectionSum({ section, transportationMethod, products, selected, exchangeRates, preCalcs, lockedExchangeRates, organization });
                            if (!sectionSum.showSection) return null;

                            const sectionSumText = (() => {
                                if (Math.round(sectionSum.min.normalized) === Math.round(sectionSum.max.normalized)) {
                                    return _.map(sectionSum.min.sum, (value, currency) => currencyToText(currency, true, money(value))).join(' | ');
                                }

                                else return `From ${_.map(sectionSum.min.sum, (value, currency) => currencyToText(currency, true, money(value))).join(' | ')}`
                            })();

                            if (sectionSumText.length === 0) return false;

                            return (
                                <TableRow>
                                    <TableCell sx={{ fontSize: '12px', fontWeight: 400 }}>
                                        {section.title}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'right' }}>
                                        {sectionSumText}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default OfferOpportunityOneOff;
export { Offer };

// Transit time, validity, route and carrier of an offer or a section's option, as label/value
// pairs, leaving out the ones that aren't set.
function offerDetails(o) {
    return _.filter([
        {
            title: 'Transit time:', value: (() => {
                const v = o.transitTime;
                if (!v) return null;
                if (_.isString(v)) return v;
                const mins = parseInt((v % 1) * 60);
                const days = parseInt(parseInt(v) / 24);
                const hours = parseInt(v) % 24;
                return [
                    days ? (days === 1 ? `${days} day` : `${days} days`) : '',
                    hours ? (hours === 1 ? `${hours} hour` : `${hours} hours`) : '',
                    mins ? (mins === 1 ? `${mins} min` : `${mins} mins`) : '',
                ].join(' ');
            })()
        },
        validityField(o),
        { title: 'Route:', value: (o.routes || []).map(r => `${r.source?.text || 'Everywhere'} to ${r.destination?.text || 'Everywhere'}`).join(', ') },
        { title: 'Carrier:', value: o.carrier }
    ], a => !!a.value);
}

// A section's charges: the per-container grid, under the containers it prices, then the charges for
// the whole shipment as a plain list of name and amount with no container columns over it, so none
// of them looks like it belongs to a container. Text rows join the list when there is one.
// Quotes rendered before the server sorted charges (no fieldScope) keep the single grid.
function SectionCharges(props) {
    const { fields, headerHtml, html, productCount } = props;
    const scopeOf = field => html.fieldScope?.[field.id];
    // The charge-name column: an equal share with the container columns, but never under a quarter
    // so long names keep room. The grid and the list use the same share, so amounts line up.
    const nameShare = Math.max(100 / ((productCount || 1) + 1), 25);
    const containerShare = (100 - nameShare) / (productCount || 1);

    if (_.isEmpty(html.fieldScope)) {
        return (
            <Table className='charge-table section-charges'>
                <TableHead>
                    <TableRow dangerouslySetInnerHTML={{ __html: headerHtml }} />
                </TableHead>
                <TableBody dangerouslySetInnerHTML={{ __html: fields.map(field => html.fields[field.id]).join('') }} />
            </Table>
        );
    }

    const priced = fields.filter(field => scopeOf(field) === 'shipment' && html.shipmentFields[field.id]);
    const grid = fields.filter(field => scopeOf(field) === 'container' && html.fields[field.id]);
    const textInList = !_.isEmpty(priced) || _.isEmpty(grid);
    const gridRows = fields.filter(field => (scopeOf(field) === 'container' || (scopeOf(field) === 'text' && !textInList)) && html.fields[field.id]);
    const listRows = fields.filter(field => (scopeOf(field) === 'shipment' || (scopeOf(field) === 'text' && textInList)) && html.shipmentFields[field.id]);

    return (
        <>
            {!_.isEmpty(gridRows) && (
                <Table className='charge-table section-charges'>
                    <colgroup>
                        <col style={{ width: `${nameShare}%` }} />
                        {_.times(productCount || 1, i => <col key={i} style={{ width: `${containerShare}%` }} />)}
                    </colgroup>
                    <TableHead>
                        <TableRow dangerouslySetInnerHTML={{ __html: headerHtml }} />
                    </TableHead>
                    <TableBody dangerouslySetInnerHTML={{ __html: gridRows.map(field => html.fields[field.id]).join('') }} />
                </Table>
            )}
            {!_.isEmpty(listRows) && (
                <Table className='charge-table section-charges shipment-charges' sx={{ marginTop: _.isEmpty(gridRows) ? '0px' : '14px' }}>
                    {/* Name and amount, the name column the same share as the grid's so the amounts line up. */}
                    <colgroup>
                        <col style={{ width: `${nameShare}%` }} />
                        <col />
                    </colgroup>
                    <TableBody dangerouslySetInnerHTML={{ __html: listRows.map(field => html.shipmentFields[field.id]).join('') }} />
                </Table>
            )}
        </>
    );
}
